import { useMutation, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import {
  IPartCancelRequest,
  IPartImagePatchRequest,
  IPartQcRequest,
  PartImageRequirementType,
} from "../models/Part";
import { IPartPublishResult } from "../models/Publishing";
import { DefaultResponseWithData } from "../models/system";

/**
 * Mutations that invalidate single-part listing cache without mounting the `/part/:id/offers` query.
 */
export default function useSinglePartMutations(partId: string | undefined) {
  const { get, post, patch, postDocs, del } = useApiHelper();
  const queryClient = useQueryClient();

  const patchPart = useMutation(
    (req: IPartQcRequest) => patch(`/part/${req.partId}`, req),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["parts", partId]);
      },
    }
  );

  // 200 carries the per-marketplace outcome even when some marketplaces refused the part;
  // only a pre-marketplace rejection (missing offer, validation) comes back as an error.
  const publish = useMutation(
    (offerId: string) =>
      post<null, DefaultResponseWithData<IPartPublishResult>>(`/part/offers/${offerId}/publish`, null),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["parts", partId]);
        queryClient.invalidateQueries(["listingStatus", partId]);
      },
    }
  );

  const createOffer = useMutation(
    (pid: string) => post(`/part/offers/default/${pid}`, null),
    {
      onSuccess: (_, pid) => {
        queryClient.invalidateQueries(["parts", pid]);
        queryClient.invalidateQueries(["parts", "multiple"]);
      },
    }
  );

  const withdraw = useMutation(
    (offerId: string) => post(`/part/offers/${offerId}/withdraw`, null),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["parts", partId]);
      },
    }
  );

  const cancel = useMutation(
    (req: IPartCancelRequest) => post(`/part/cancelorder`, req),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["parts", partId]);
      },
    }
  );

  const uploadPicture = useMutation(
    ({
      data,
      imageType,
    }: {
      data: FormData;
      imageType?: PartImageRequirementType;
    }) =>
      postDocs(
        // Compare against null explicitly — PartImageRequirementType.Label is 0, so a
        // truthiness check drops the type and uploads an untyped image. Untyped images
        // are still listed (nothing filters on ImageType), so this silently put Label
        // shots onto eBay.
        `/part/${partId}/upload${
          imageType !== undefined && imageType !== null ? `?imageType=${imageType}` : ""
        }`,
        data
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["parts", partId]);
        queryClient.invalidateQueries(["partDetails", partId]);
        queryClient.invalidateQueries(["pagedTable"]);
      },
    }
  );

  const autoReorderImages = useMutation(
    (ids: any) => post(`/part/images/autoOrder`, ids),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(["job"]);
        if (Array.isArray(variables)) {
          variables.forEach((id: string) => {
            queryClient.invalidateQueries(["parts", id]);
            queryClient.refetchQueries(["parts", id]);
            queryClient.invalidateQueries(["partDetails", id]);
            queryClient.refetchQueries(["partDetails", id]);
          });
        }
        queryClient.invalidateQueries(["parts"]);
        queryClient.invalidateQueries(["partDetails", partId]);
        queryClient.invalidateQueries(["pagedTable"]);
      },
    }
  );

  const removeImage = useMutation(
    (imageId: string) => del(`/part/images/${imageId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["parts", partId]);
        queryClient.invalidateQueries(["partDetails", partId]);
        queryClient.invalidateQueries(["pagedTable"]);
      },
    }
  );

  const removePictureBg = useMutation(
    (imageId: string | undefined) => get(`/part/images/${imageId}/removebg`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["parts", partId]);
        queryClient.invalidateQueries(["partDetails", partId]);
        queryClient.invalidateQueries(["pagedTable"]);
      },
    }
  );

  const patchPicture = useMutation(
    (data: IPartImagePatchRequest[]) => patch(`/part/images/bulk`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["parts", partId]);
        queryClient.refetchQueries(["parts", partId]);
        queryClient.invalidateQueries(["partDetails", partId]);
        queryClient.refetchQueries(["partDetails", partId]);
        queryClient.invalidateQueries(["pagedTable"]);
      },
    }
  );

  return {
    patchPart,
    publish,
    createOffer,
    withdraw,
    cancel,
    uploadPicture,
    autoReorderImages,
    removeImage,
    patchPicture,
    removePictureBg,
  };
}
